import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { adminAuth, AdminUser } from '../api/admin';
import axiosInstance from '../api/axios';

interface AdminAuthContextType {
  adminUser: AdminUser | null;
  isAdminAuthenticated: boolean;
  adminLogin: (email: string, password: string) => Promise<void>;
  adminLogout: () => void;
  isLoading: boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const storedUser = localStorage.getItem('adminUser');

    if (token && storedUser) {
      setAdminUser(JSON.parse(storedUser));
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    setIsLoading(false);
  }, []);

  const adminLogin = useCallback(async (email: string, password: string) => {
    try {
      const response = await adminAuth.login({ email, password });
      const { access_token, user } = response;

      localStorage.setItem('adminToken', access_token);
      localStorage.setItem('adminUser', JSON.stringify(user));

      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      setAdminUser(user);
    } catch (error) {
      throw error;
    }
  }, []);

  const adminLogout = useCallback(() => {
    adminAuth.logout();
    delete axiosInstance.defaults.headers.common['Authorization'];
    setAdminUser(null);
  }, []);

  const value = {
    adminUser,
    isAdminAuthenticated: !!adminUser,
    adminLogin,
    adminLogout,
    isLoading,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};